import { useCallback, useState, useEffect } from 'react';
import { MediaObject } from 'src/api/services/MediaClient';
import { useMediaUpload } from 'src/pages/media-library/context/media-upload-context';
import { useToast } from 'src/components/ui/use-toast';
import { Api } from 'src/api';

interface SaveToMediaLibraryResult {
  saveToMediaLibrary: (media: MediaObject) => Promise<void>;
  isSaving: boolean;
  isAlreadySaved: (media: MediaObject) => Promise<boolean>;
  isAlreadySavedSync: (media: MediaObject) => boolean;
}

/**
 * Custom hook to save revision media files to the media library root folder
 */
export const useSaveToMediaLibrary = (): SaveToMediaLibraryResult => {
  const [isSaving, setIsSaving] = useState(false);
  const [savedFiles, setSavedFiles] = useState<Set<string>>(new Set());
  const { handleUpload } = useMediaUpload();
  const { toast } = useToast();
  
  const STORAGE_KEY = 'revision_saved_files';
  
  // Load saved files from sessionStorage on mount
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsedFiles = JSON.parse(stored);
        setSavedFiles(new Set(parsedFiles));
      }
    } catch (error) {
      setSavedFiles(new Set());
    }
  }, []);
  
  // Save to sessionStorage whenever savedFiles changes
  const updateSavedFiles = useCallback((newSavedFiles: Set<string>) => {
    try {
      setSavedFiles(newSavedFiles);
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(newSavedFiles)));
    } catch (error) {
      // Silently handle storage errors
    }
  }, []);
  
  // Create a unique identifier for a media file
  const getMediaIdentifier = useCallback((media: MediaObject): string => {
    return `${media.id}_${media.file_name}`;
  }, []);
  
  // Check if file has already been saved (session + media library verification)
  const isAlreadySaved = useCallback(async (media: MediaObject): Promise<boolean> => {
    const identifier = getMediaIdentifier(media);
    
    // First check session storage for quick response
    if (!savedFiles.has(identifier)) {
      return false; // Not in session storage, definitely not saved
    }
    
    // If in session storage, verify it still exists in media library
    try {
      const mediaLibraryFiles = await Api.media.getMediaLibrary();
      
      if (!mediaLibraryFiles || !Array.isArray(mediaLibraryFiles)) {
        // If we can't check media library, trust session storage
        return true;
      }
      
      // Check if file with same name exists in media library
      const fileExists = mediaLibraryFiles.some((libraryFile: any) => 
        libraryFile.file_name === media.file_name
      );
      
      if (!fileExists) {
        // File was deleted from media library, remove from session tracking
        const newSavedFiles = new Set(savedFiles);
        newSavedFiles.delete(identifier);
        updateSavedFiles(newSavedFiles);
        return false;
      }
      
      // File exists in both session storage and media library
      return true;
    } catch (error) {
      // If verification fails, trust session storage to avoid false negatives
      return true;
    }
  }, [savedFiles, getMediaIdentifier, updateSavedFiles]);

  // Synchronous version - just checks session storage without API call
  const isAlreadySavedSync = useCallback((media: MediaObject): boolean => {
    const identifier = getMediaIdentifier(media);
    return savedFiles.has(identifier);
  }, [savedFiles, getMediaIdentifier]);


  const saveToMediaLibrary = useCallback(
    async (media: MediaObject) => {
      if (!media || !media.index_url) {
        toast({
          title: 'Error',
          description: 'No valid media file to save',
          variant: 'destructive',
        });
        return;
      }

      // Check if already saved
      if (await isAlreadySaved(media)) {
        toast({
          title: 'Already Saved',
          description: `"${media.file_name}" has already been saved to your Media Library`,
          variant: 'info',
        });
        return;
      }

      setIsSaving(true);

      try {
        // Download the file from the revision
        const response = await fetch(media.index_url);
        if (!response.ok) {
          throw new Error(`Failed to fetch media file: ${response.status} ${response.statusText}`);
        }

        const blob = await response.blob();
        
        // Create a File object from the blob - works for ANY file type
        const file = new File([blob], media.file_name || `revision-file-${Date.now()}`, {
          type: blob.type || 'application/octet-stream',
        });

        // Upload to media library root folder (empty path array means root)
        await new Promise<void>((resolve, reject) => {
          let uploadCompleted = false;
          const timeoutId = setTimeout(() => {
            if (!uploadCompleted) {
              uploadCompleted = true;
              reject(new Error('Upload timeout - file may still be processing'));
            }
          }, 60000); // 60 second timeout
          
          handleUpload({
            files: [file],
            userPath: [], // Root folder
            onSuccess: (items) => {
              if (!uploadCompleted) {
                uploadCompleted = true;
                clearTimeout(timeoutId);
                
                // Mark file as saved in our tracking
                const identifier = getMediaIdentifier(media);
                const newSavedFiles = new Set(savedFiles);
                newSavedFiles.add(identifier);
                updateSavedFiles(newSavedFiles);
                
                toast({
                  title: 'Success',
                  description: `"${file.name}" has been saved to your Media Library`,
                });
                resolve();
              }
            },
            onError: (error) => {
              if (!uploadCompleted) {
                uploadCompleted = true;
                clearTimeout(timeoutId);
                toast({
                  title: 'Upload Failed',
                  description: `Failed to save file to Media Library. Error: ${error?.message || 'Unknown error'}`,
                  variant: 'destructive',
                });
                reject(error);
              }
            },
            onSingleFileError: (item, error) => {
              if (!uploadCompleted) {
                uploadCompleted = true;
                clearTimeout(timeoutId);
                toast({
                  title: 'Upload Error',
                  description: `Failed to upload "${item.fileName}". Error: ${error?.message || 'Unknown error'}`,
                  variant: 'destructive',
                });
                reject(error);
              }
            },
            onSingleFileSuccess: (item, mediaObject) => {
// Don't resolve here, wait for overall onSuccess
            },
          });
        });
      } catch (error) {
        toast({
          title: 'Save Failed',
          description: 'Failed to save file to Media Library. Please try again.',
          variant: 'destructive',
        });
      } finally {
        setIsSaving(false);
      }
    },
    [handleUpload, toast, isAlreadySaved, getMediaIdentifier, savedFiles, updateSavedFiles],
  );

  return {
    saveToMediaLibrary,
    isSaving,
    isAlreadySaved,
    isAlreadySavedSync,
  };
};
